import React, { MutableRefObject, useState } from 'react';
import {
  Feedback,
  fieldFeedbackContainer,
  requiredLabel,
} from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import { CurrencyCode } from '@noths/polaris-dev-ts-types';

import { SelectionPayload } from 'src/redux/personalisation';
import { ProductOption, ProductOptionValue } from 'src/types';
import { getFormattedPersonalisationAmount } from 'src/utils';
import * as copy from '../../constants/copy';
import { MultipleOptionSelectionDrawerContainer } from '../../containers/MultipleOptionSelectionDrawerContainer';
import * as styles from '../../styles/Personalisation.styles';

export const SELECTION_MULTIPLE = 'selection-multiple';
export const CTA_APPLY_CLASSNAME = 'cta-apply-button';

export const DRAWER_DESCRIPTOR_ID = 'drawer-description';

export interface OwnProps extends ProductOption {
  displayValidation: boolean | undefined;
  forwardedRef: MutableRefObject<HTMLUListElement | null>;
}

export interface ContainerProps {
  currency: CurrencyCode;
  error: boolean;
  selectedOption?: ProductOptionValue[];
}

export interface DispatchProps {
  selectPersonalisation: (selection: SelectionPayload) => void;
  deselectPersonalisation: (selection: SelectionPayload) => void;
}

type Props = ContainerProps & DispatchProps & OwnProps;

export const MultipleOptionSelection = ({
  currency,
  error,
  selectPersonalisation,
  deselectPersonalisation,
  ...props
}: Props) => {
  const appliedValue = props.selectedOption?.[0];
  const [isExpanded, setExpanded] = useState(false);
  const elementId = `selection-multiple-${props.id}`;

  const applySelection = (selectedValue: ProductOptionValue | null) => {
    setExpanded(!isExpanded);

    if (!selectedValue && !appliedValue) return;

    if (selectedValue) {
      selectPersonalisation({
        optionId: props.id,
        optionValue: selectedValue!,
        type: 'single-selection',
      });
      return;
    }

    deselectPersonalisation({
      optionId: props.id,
      optionValue: appliedValue!,
      type: 'single-selection',
    });
  };

  const discardSelection = () => {
    setExpanded(!isExpanded);
  };

  const { isNegative = false, value: formattedPriceValue = '' } = appliedValue?.priceModifier
    ? getFormattedPersonalisationAmount(currency, appliedValue.priceModifier)
    : {};

  return (
    <section
      css={[styles.selectionContainer, styles.multipleSelectionContainer]}
      data-has-error={!!error}
      data-is-valid={appliedValue && !error}
    >
      {props.required && (
        <p css={requiredLabel} data-has-error={error} aria-hidden="true">
          Required
        </p>
      )}
      <button
        css={styles.wrapperButton}
        type="button"
        onClick={() => setExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls={elementId}
      >
        <p css={styles.legend} dangerouslySetInnerHTML={{ __html: props.name }} />
        <div className={SELECTION_MULTIPLE}>
          <div css={[styles.inputSelect, styles.inputOpen]}>
            <span css={visuallyHidden}>,</span>
            <span dangerouslySetInnerHTML={{ __html: appliedValue?.name || copy.SELECT_COPY }} />

            {appliedValue?.priceModifier && (
              <>
                <span css={visuallyHidden}>,</span>
                <span css={styles.priceModifier}>
                  <span data-is-negative={isNegative} css={styles.priceOperator}>
                    {isNegative ? <>&minus;</> : <>&#43;</>}
                  </span>
                  <span>{formattedPriceValue.replace('-', '')}</span>
                </span>
              </>
            )}
          </div>
          <div css={[fieldFeedbackContainer, styles.feedbackContainer]}>
            {error && <Feedback name={copy.REQUIRED_SELECTION_COPY} error iconAlign="left" />}
          </div>
        </div>
      </button>
      <MultipleOptionSelectionDrawerContainer
        currency={currency}
        onConfirm={applySelection}
        onClose={discardSelection}
        elementId={elementId}
        isExpanded={isExpanded}
        option={props}
        appliedValue={appliedValue}
        target={props.forwardedRef as MutableRefObject<HTMLUListElement>}
      />
    </section>
  );
};
